// Customers Overview — the /customers index page.
//
// Ported from design/Customers Overview.html (Claude.ai design handoff,
// extracted into design/_customers_overview/). Two tabs: a dashboard with
// KPIs / leaderboard / AM book / alerts / renewal horizon, and a
// filter+sort list of every customer.
//
// Backend gaps not filled in this PR (the design mocks these — they aren't
// stored yet):
//   - industry, size (enterprise / mid / growth)        → omitted columns
//   - per-customer hours/invoiced history (sparklines)  → omitted
//   - Invoiced YTD aggregation                          → KPI omitted
//
// Aggregations we compute client-side from /customers + /projects +
// /contracts/agreements: project counts, hours rollups, active-contract
// pill, AM book balance, alerts, upcoming-renewal buckets. Edit drawer
// reachable from a hover-only pencil per row (rows themselves navigate
// to the Customer 360 page, matching the design).

import { useMemo, useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Plus, Pencil, Download, AlertCircle, AlertTriangle, ChevronDown,
  Search, X, LayoutGrid, List as ListIcon, Rows3, Rows4,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type { Customer, Project, Agreement } from '@/types/api'
import { CustomerDrawer } from './CustomerDrawer'
import './customersIndex.css'

// ─── Date helpers ─────────────────────────────────────────────────────────────

const TODAY = new Date()

function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 86_400_000)
}

function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s)
  return Number.isNaN(d.getTime()) ? null : d
}

function fmtMonthShort(d: Date): string {
  return d.toLocaleDateString('en-GB', { month: 'short' })
}

function fmtMonthYearShort(d: Date): string {
  const m = d.toLocaleDateString('en-GB', { month: 'short' })
  return `${m} ${String(d.getFullYear()).slice(2)}`
}

// ─── Avatar / initials helpers (mirrors CustomerOverviewPage) ─────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function hueFor(name: string): number {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return h % 360
}

function customerLogoStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.7 0.16 ${h}), oklch(0.55 0.18 ${(h + 30) % 360}))`,
  }
}

function avatarStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.6 0.14 ${h}), oklch(0.5 0.16 ${(h + 60) % 360}))`,
  }
}

// ─── Number formatters ────────────────────────────────────────────────────────

function fmtHours(n: number): string {
  return n.toLocaleString('en-GB', { maximumFractionDigits: 0 })
}

function fmt1(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

// ─── Derived per-customer stats ───────────────────────────────────────────────

interface ContractInfo {
  agreement: Agreement
  pillKind:  'good' | 'warn' | 'danger' | 'neutral'
  label:     string
}

function contractInfoFor(agreements: Agreement[], customerId: number): ContractInfo | null {
  // "Active" in the design pill sense = status active OR renewal-due, and
  // the latest by endDate. Expired / draft don't get a pill on the row.
  const mine = agreements
    .filter(a => a.customerId === customerId
              && (a.status === 'active' || a.status === 'renewal-due'))
    .sort((a, b) => {
      const ae = parseDate(a.endDate)?.getTime() ?? 0
      const be = parseDate(b.endDate)?.getTime() ?? 0
      return be - ae
    })
  if (mine.length === 0) return null
  const a = mine[0]
  const end = parseDate(a.endDate)
  const daysToEnd = end ? daysBetween(TODAY, end) : null

  let pillKind: ContractInfo['pillKind'] = 'good'
  const label = formatContractLabel(a, end, daysToEnd)
  if (daysToEnd != null && daysToEnd < 90)  pillKind = 'danger'
  else if (daysToEnd != null && daysToEnd < 180) pillKind = 'warn'

  return { agreement: a, pillKind, label }
}

function formatContractLabel(a: Agreement, end: Date | null, daysToEnd: number | null): string {
  const kind = contractTypeLabel(a.contractType)
  if (!end) return kind
  if (daysToEnd != null && daysToEnd < 180) {
    return `${kind} · ${fmtMonthShort(end)}`
  }
  return `${kind} · '${String(end.getFullYear()).slice(2)}`
}

function contractTypeLabel(t: Agreement['contractType']): string {
  switch (t) {
    case 'msa':       return 'MSA'
    case 'sow':       return 'SOW'
    case 'framework': return 'Frame'
    case 'service':   return 'Service'
    case 'nda':       return 'NDA'
    case 'dpa':       return 'DPA'
    default:          return 'Other'
  }
}

interface CustomerStat {
  customer:          Customer
  projects:          Project[]
  activeProjects:    number
  totalHours:        number
  budgetHours:       number | null
  utilizationPct:    number | null
  contract:          ContractInfo | null
  daysSinceWorkedOn: number | null
}

function buildStats(
  customers: Customer[],
  projects: Project[],
  agreements: Agreement[],
): CustomerStat[] {
  return customers.map(c => {
    const mine = projects.filter(p => p.customerId === c.id)
    const activeProjects = mine.filter(p => p.status === 'active').length
    const totalHours = mine.reduce((s, p) => s + (p.totalHours ?? 0), 0)
    const budgetHours = mine.reduce<number | null>((acc, p) => {
      if (p.budgetHours == null) return acc
      return (acc ?? 0) + p.budgetHours
    }, null)
    const utilizationPct = budgetHours && budgetHours > 0
      ? Math.round((totalHours / budgetHours) * 100)
      : null
    const daysSinceWorkedOn = mine.reduce<number | null>((acc, p) => {
      if (p.daysSinceWorkedOn == null) return acc
      return acc == null ? p.daysSinceWorkedOn : Math.min(acc, p.daysSinceWorkedOn)
    }, null)
    return {
      customer: c,
      projects: mine,
      activeProjects,
      totalHours,
      budgetHours,
      utilizationPct,
      contract: contractInfoFor(agreements, c.id),
      daysSinceWorkedOn,
    }
  })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type TabKey = 'dashboard' | 'list'

export default function CustomersModule() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<TabKey>(() => {
    try { return (localStorage.getItem('customers-tab') as TabKey) ?? 'dashboard' }
    catch { return 'dashboard' }
  })
  useEffect(() => {
    try { localStorage.setItem('customers-tab', tab) } catch { /* ignore */ }
  }, [tab])

  const [editing, setEditing] = useState<Customer | null>(null)
  const [creating, setCreating] = useState(false)

  // includeInactive=true so the List tab can filter to inactive customers
  // (WI-11999). Active-only widgets on the dashboard tab compute against
  // `activeStats` below.
  const { data: customers = [], isLoading, isError } = useQuery({
    queryKey: ['customers', { includeInactive: true }],
    queryFn:  () => api.get<Customer[]>('/customers?includeInactive=true'),
    staleTime: 5 * 60 * 1000,
  })
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<Project[]>('/projects'),
    staleTime: 5 * 60 * 1000,
  })
  const { data: agreements = [] } = useQuery({
    queryKey: ['contracts', 'agreements'],
    queryFn:  () => api.get<Agreement[]>('/contracts/agreements'),
  })

  const stats = useMemo(
    () => buildStats(customers, projects, agreements),
    [customers, projects, agreements],
  )
  // Dashboard widgets (totals, leaderboard, AM book, alerts) operate on
  // active customers so totals don't quietly grow when someone deactivates
  // a customer. The list tab gets the full set and filters by status.
  const activeStats = useMemo(
    () => stats.filter(s => s.customer.isActive),
    [stats],
  )
  const activeAgreements = useMemo(() => {
    const activeIds = new Set(activeStats.map(s => s.customer.id))
    return agreements.filter(a => activeIds.has(a.customerId))
  }, [agreements, activeStats])

  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] })
    setEditing(null); setCreating(false)
  }

  return (
    <div className="page">
      <PageHeader onCreate={() => setCreating(true)} />

      <div className="cov-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'dashboard'}
          className={tab === 'dashboard' ? 'on' : ''}
          onClick={() => setTab('dashboard')}
        >
          <LayoutGrid size={15} />
          Dashboard
        </button>
        <button
          role="tab"
          aria-selected={tab === 'list'}
          className={tab === 'list' ? 'on' : ''}
          onClick={() => setTab('list')}
        >
          <ListIcon size={15} />
          List <span className="count">{customers.length}</span>
        </button>
      </div>

      {isLoading && (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>
          Loading customers…
        </div>
      )}
      {isError && (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13, color: 'var(--ns-err)' }}>
          Couldn't load customers. Try refreshing.
        </div>
      )}

      {!isLoading && !isError && tab === 'dashboard' && (
        <DashboardTab stats={activeStats} agreements={activeAgreements} />
      )}
      {!isLoading && !isError && tab === 'list' && (
        <ListTab stats={stats} onEdit={c => setEditing(c)} />
      )}

      {(editing || creating) && (
        <CustomerDrawer
          customer={editing}
          onClose={() => { setEditing(null); setCreating(false) }}
          onSaved={onSaved}
        />
      )}
    </div>
  )
}

// ─── Page header ──────────────────────────────────────────────────────────────

function PageHeader({ onCreate }: { onCreate: () => void }) {
  // The period seg is visual-only — none of the KPIs are period-scoped yet.
  // Wire to a date-range query param when hours/invoiced YTD aggregations land.
  const [period, setPeriod] = useState<'month' | 'quarter' | 'ytd' | 'year'>('ytd')
  return (
    <div className="cov-page-head">
      <div>
        <h1>Customers</h1>
        <div className="sub">All customers, contracts, and engagement at a glance</div>
      </div>
      <div className="cov-head-actions">
        <div className="cov-seg" role="tablist" aria-label="Period">
          {(['month', 'quarter', 'ytd', 'year'] as const).map(p => (
            <button
              key={p}
              className={period === p ? 'on' : ''}
              onClick={() => setPeriod(p)}
            >
              {p === 'ytd' ? 'YTD' : p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <button className="btn" disabled title="CSV export — coming with next milestone">
          <Download size={14} />Export
        </button>
        <button className="btn btn-brand" style={{ gap: 6 }} onClick={onCreate}>
          <Plus size={14} />New customer
        </button>
      </div>
    </div>
  )
}

// ─── Dashboard tab ────────────────────────────────────────────────────────────

function DashboardTab({
  stats, agreements,
}: {
  stats:      CustomerStat[]
  agreements: Agreement[]
}) {
  return (
    <>
      <KpiStrip stats={stats} />
      <Leaderboard stats={stats} />
      <section className="cov-dash-row">
        <AmBookCard stats={stats} />
        <AlertsCard stats={stats} />
      </section>
      <RenewalHorizonCard agreements={agreements} />
    </>
  )
}

// ─── KPIs ─────────────────────────────────────────────────────────────────────

function KpiStrip({ stats }: { stats: CustomerStat[] }) {
  const totalCustomers      = stats.length
  const activeProjects      = stats.reduce((s, x) => s + x.activeProjects, 0)
  const customersWithActive = stats.filter(s => s.activeProjects > 0).length
  const customersDormant    = stats.filter(s => s.activeProjects === 0).length

  const totalHours = stats.reduce((s, x) => s + x.totalHours, 0)
  const totalBudget = stats.reduce((s, x) => s + (x.budgetHours ?? 0), 0)
  const utilPct = totalBudget > 0 ? Math.round((totalHours / totalBudget) * 100) : null

  const withContract  = stats.filter(s => s.contract != null).length
  const coveragePct   = totalCustomers > 0
    ? Math.round((withContract / totalCustomers) * 100)
    : 0
  const expiringSoon  = stats.filter(s => {
    const end = parseDate(s.contract?.agreement.endDate ?? null)
    if (!end) return false
    const d = daysBetween(TODAY, end)
    return d >= 0 && d < 90
  }).length
  const openEnded = stats.filter(s => s.contract != null && !s.contract.agreement.endDate).length

  return (
    <section className="cov-kpis">
      <div className="cov-kpi">
        <div className="cov-kpi-label">Total customers</div>
        <div className="cov-kpi-val">{totalCustomers}</div>
        {totalCustomers > 0 && (
          <>
            <div className="cov-kpi-stack" aria-hidden="true">
              <div style={{
                width: `${(customersWithActive / totalCustomers) * 100}%`,
                background: 'var(--ns-ok)',
              }} title={`${customersWithActive} active`} />
              <div style={{
                width: `${(customersDormant / totalCustomers) * 100}%`,
                background: 'var(--ns-warn)',
              }} title={`${customersDormant} dormant`} />
            </div>
            <div className="cov-kpi-legend">
              <span style={{ color: 'var(--ns-ok)' }}>{customersWithActive} with active work</span>
              <span style={{ color: 'var(--ns-warn)' }}>{customersDormant} idle</span>
            </div>
          </>
        )}
      </div>

      <div className="cov-kpi">
        <div className="cov-kpi-label">Active projects</div>
        <div className="cov-kpi-val">
          {activeProjects} <span className="unit">across {customersWithActive}</span>
        </div>
        <div className="cov-kpi-sub">
          {customersDormant > 0
            ? `${customersDormant} customer${customersDormant === 1 ? '' : 's'} without active work`
            : 'Every customer has active work'}
        </div>
      </div>

      <div className="cov-kpi featured">
        <div className="cov-kpi-label">Hours logged · all-time</div>
        <div className="cov-kpi-val">
          {fmtHours(totalHours)} <span className="unit">h</span>
        </div>
        <div className="cov-kpi-sub">
          {utilPct != null
            ? `${utilPct}% of ${fmtHours(totalBudget)} h budget`
            : 'No budgets set'}
        </div>
        {utilPct != null && (
          <div className="cov-kpi-stack" aria-hidden="true">
            <div style={{
              width: `${Math.min(100, utilPct)}%`,
              background: 'oklch(from var(--ns-surface) l c h / 0.85)',
            }} />
          </div>
        )}
      </div>

      <div className="cov-kpi">
        <div className="cov-kpi-label">Contract coverage</div>
        <div className="cov-kpi-val">
          {coveragePct}<span className="unit">%</span>
        </div>
        <div className={`cov-kpi-sub ${expiringSoon > 0 ? 'warn' : ''}`}>
          {withContract} active · {expiringSoon} expiring &lt; 90d · {openEnded} open-ended
        </div>
        {totalCustomers > 0 && (
          <div className="cov-kpi-stack" aria-hidden="true">
            <div style={{
              width: `${(withContract / totalCustomers) * 100}%`,
              background: 'var(--ns-ok)',
            }} />
            <div style={{
              width: `${(expiringSoon / totalCustomers) * 100}%`,
              background: 'var(--ns-warn)',
            }} />
          </div>
        )}
      </div>
    </section>
  )
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────

function Leaderboard({ stats }: { stats: CustomerStat[] }) {
  const [expanded, setExpanded] = useState(false)
  const ranked = useMemo(
    () => [...stats].sort((a, b) => b.totalHours - a.totalHours),
    [stats],
  )
  const visible = expanded ? ranked : ranked.slice(0, 8)

  if (ranked.length === 0) return null

  return (
    <section className="cov-card" style={{ marginBottom: 16 }}>
      <div className="cov-card-head">
        <div>
          <div className="cov-card-title">Top customers · by hours logged</div>
          <div className="cov-card-sub">
            Sorted by total project hours · click a customer to open their 360
          </div>
        </div>
        {ranked.length > 8 && (
          <button
            className="cov-card-link"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? 'Show top 8 ↑' : `Show all ${ranked.length} →`}
          </button>
        )}
      </div>
      <div className="cov-lboard">
        {visible.map(s => <LeaderboardRow key={s.customer.id} stat={s} />)}
      </div>
    </section>
  )
}

function LeaderboardRow({ stat }: { stat: CustomerStat }) {
  const { customer: c, projects, totalHours, budgetHours, contract } = stat
  const utilization = stat.utilizationPct
  const utilWarn = utilization != null && utilization > 60

  return (
    <Link
      to="/customers/$id"
      params={{ id: String(c.id) }}
      className="cov-lb-row"
    >
      <div className="cov-lb-logo" style={customerLogoStyle(c.name)}>
        {initials(c.name)}
      </div>
      <div className="cov-lb-name">
        <span className="nm">{c.name}</span>
        <span className="badges">
          {c.city && <span className="cov-badge">{c.city}</span>}
          {c.country && c.country !== 'Sweden' && <span className="cov-badge">{c.country}</span>}
        </span>
      </div>
      <div className="cov-lb-mgr">
        {c.accountManagerName ? (
          <>
            <div className="av" style={avatarStyle(c.accountManagerName)}>
              {initials(c.accountManagerName)}
            </div>
            {c.accountManagerName}
          </>
        ) : (
          <span style={{ color: 'var(--ns-text-subtle)', fontSize: 11 }}>No AM</span>
        )}
      </div>
      <ContractPill contract={contract} />
      <div className="cov-lb-dual">
        <div className="line">
          <span>Hours</span>
          <span>
            <span className="val">{fmtHours(totalHours)}</span>
            {budgetHours != null && <> / {fmtHours(budgetHours)} h</>}
          </span>
        </div>
        {budgetHours != null && budgetHours > 0 && (
          <div className={`bar ${utilWarn ? 'warn' : ''}`}>
            <div style={{ width: `${Math.min(100, utilization ?? 0)}%` }} />
          </div>
        )}
      </div>
      <div className="cov-lb-rev">
        <div>
          <span className="big">{fmt1(totalHours)}</span><span className="unit">h</span>
        </div>
        <div className="sub">
          {projects.filter(p => p.status === 'active').length} active proj
        </div>
      </div>
    </Link>
  )
}

function ContractPill({ contract }: { contract: ContractInfo | null }) {
  if (!contract) return <span className="cov-pill cov-pill-neutral">None</span>
  const cls =
    contract.pillKind === 'good'    ? 'cov-pill-good' :
    contract.pillKind === 'warn'    ? 'cov-pill-warn' :
    contract.pillKind === 'danger'  ? 'cov-pill-warn' :
    'cov-pill-neutral'
  return <span className={`cov-pill ${cls}`}>{contract.label}</span>
}

// ─── AM book balance ──────────────────────────────────────────────────────────

interface AmBucket {
  amId:        number
  amName:      string
  customerIds: number[]
  customerNames: string[]
  totalHours:  number
}

function AmBookCard({ stats }: { stats: CustomerStat[] }) {
  const buckets = useMemo<AmBucket[]>(() => {
    const map = new Map<number, AmBucket>()
    for (const s of stats) {
      const id = s.customer.accountManagerId
      const name = s.customer.accountManagerName
      if (id == null || !name) continue
      const b = map.get(id) ?? {
        amId: id, amName: name, customerIds: [], customerNames: [], totalHours: 0,
      }
      b.customerIds.push(s.customer.id)
      b.customerNames.push(s.customer.name)
      b.totalHours += s.totalHours
      map.set(id, b)
    }
    return [...map.values()].sort((a, b) => b.totalHours - a.totalHours)
  }, [stats])

  const maxHours = buckets[0]?.totalHours ?? 0

  return (
    <div className="cov-card">
      <div className="cov-card-head">
        <div>
          <div className="cov-card-title">Account managers · book balance</div>
          <div className="cov-card-sub">Customers and hours per AM</div>
        </div>
      </div>
      <div className="cov-card-body" style={{ padding: '14px 18px' }}>
        {buckets.length === 0 ? (
          <div className="muted" style={{ fontSize: 12.5 }}>
            No account managers assigned yet.
          </div>
        ) : (
          <div className="cov-am-book">
            {buckets.map(b => (
              <div className="cov-am-row" key={b.amId}>
                <div className="av" style={avatarStyle(b.amName)}>{initials(b.amName)}</div>
                <div>
                  <div className="nm">{b.amName}</div>
                  <div className="meta">
                    {b.customerIds.length} customer{b.customerIds.length === 1 ? '' : 's'} ·{' '}
                    {b.customerNames.slice(0, 4).join(', ')}
                    {b.customerNames.length > 4 && ` +${b.customerNames.length - 4}`}
                  </div>
                </div>
                <div className="rev">{fmtHours(b.totalHours)} <span className="unit">h</span></div>
                <div className="cov-am-bar">
                  <div style={{ width: maxHours > 0 ? `${(b.totalHours / maxHours) * 100}%` : '0%' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Needs-attention alerts ───────────────────────────────────────────────────

interface AlertItem {
  key:   string
  level: 'warn' | 'danger'
  msg:   React.ReactNode
  when?: string
}

function AlertCustomerLink({ customer }: { customer: Customer }) {
  return (
    <Link
      to="/customers/$id"
      params={{ id: String(customer.id) }}
      className="cov-alert-customer"
    >
      {customer.name}
    </Link>
  )
}

function AlertsCard({ stats }: { stats: CustomerStat[] }) {
  const alerts = useMemo<AlertItem[]>(() => {
    const out: AlertItem[] = []

    for (const s of stats) {
      const end = parseDate(s.contract?.agreement.endDate ?? null)
      if (end) {
        const d = daysBetween(TODAY, end)
        if (d >= 0 && d < 90) {
          out.push({
            key:   `expiring-${s.customer.id}`,
            level: 'warn',
            msg:   <><AlertCustomerLink customer={s.customer} /> · {contractTypeLabel(s.contract?.agreement.contractType ?? 'other')} expires in {d}d</>,
            when:  end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
          })
        }
      }
    }

    for (const s of stats) {
      if (s.customer.isActive
       && s.activeProjects === 0
       && s.daysSinceWorkedOn != null
       && s.daysSinceWorkedOn > 30) {
        out.push({
          key:   `dormant-${s.customer.id}`,
          level: 'danger',
          msg:   <><AlertCustomerLink customer={s.customer} /> · no activity for {s.daysSinceWorkedOn} days</>,
        })
      }
    }

    return out.slice(0, 6)
  }, [stats])

  return (
    <div className="cov-card">
      <div className="cov-card-head">
        <div>
          <div className="cov-card-title">Needs attention</div>
          <div className="cov-card-sub">
            {alerts.length === 0
              ? 'Nothing urgent right now'
              : `${alerts.length} customer-level signal${alerts.length === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>
      <div className="cov-card-body" style={{ padding: '14px 18px' }}>
        {alerts.length === 0 ? (
          <div className="muted" style={{ fontSize: 12.5 }}>
            No expiring contracts and no dormant customers.
          </div>
        ) : (
          <div className="cov-alerts">
            {alerts.map(a => (
              <div key={a.key} className={`cov-alert-row ${a.level === 'danger' ? 'danger' : ''}`}>
                <span className="ic">
                  {a.level === 'danger'
                    ? <AlertTriangle size={13} />
                    : <AlertCircle size={13} />}
                </span>
                <span className="msg">{a.msg}</span>
                {a.when && <span className="when">{a.when}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Renewal horizon — month buckets for next 12 months ───────────────────────

interface RenewalBucket {
  d:      Date
  label:  string
  agreements: { a: Agreement; daysToEnd: number }[]
  totalValue: number
  totalLabel: string
}

function RenewalHorizonCard({ agreements }: { agreements: Agreement[] }) {
  const buckets = useMemo<RenewalBucket[]>(() => {
    const out: RenewalBucket[] = []
    for (let i = 0; i < 12; i++) {
      const d = new Date(TODAY.getFullYear(), TODAY.getMonth() + i, 1)
      out.push({
        d,
        label: fmtMonthYearShort(d),
        agreements: [],
        totalValue: 0,
        totalLabel: '',
      })
    }
    for (const a of agreements) {
      if (a.status !== 'active' && a.status !== 'renewal-due') continue
      const end = parseDate(a.endDate)
      if (!end) continue
      const monthsAhead = (end.getFullYear() - TODAY.getFullYear()) * 12
                       + (end.getMonth() - TODAY.getMonth())
      if (monthsAhead < 0 || monthsAhead >= 12) continue
      const days = daysBetween(TODAY, end)
      out[monthsAhead].agreements.push({ a, daysToEnd: days })
      if (a.value != null) out[monthsAhead].totalValue += a.value
    }
    for (const b of out) {
      if (b.totalValue > 0) {
        const k = b.totalValue / 1000
        b.totalLabel = k >= 1000 ? `${(k / 1000).toFixed(1)} M` : `${Math.round(k)} k`
      }
    }
    return out
  }, [agreements])

  const total      = buckets.reduce((s, b) => s + b.agreements.length, 0)
  const totalValue = buckets.reduce((s, b) => s + b.totalValue, 0)
  const valueLabel = totalValue >= 1_000_000
    ? `${(totalValue / 1_000_000).toFixed(1)} M`
    : `${Math.round(totalValue / 1000)} k`

  const expiring90 = buckets.slice(0, 3).reduce((s, b) => s + b.totalValue, 0)
  const expiring90Label = expiring90 >= 1_000_000
    ? `${(expiring90 / 1_000_000).toFixed(1)} M`
    : `${Math.round(expiring90 / 1000)} k`

  if (total === 0) {
    return (
      <section className="cov-card" style={{ marginBottom: 16 }}>
        <div className="cov-card-head">
          <div>
            <div className="cov-card-title">Contract renewal horizon · next 12 months</div>
            <div className="cov-card-sub">Nothing renews in the next 12 months</div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="cov-card" style={{ marginBottom: 16 }}>
      <div className="cov-card-head">
        <div>
          <div className="cov-card-title">Contract renewal horizon · next 12 months</div>
          <div className="cov-card-sub">
            {total} agreement{total === 1 ? '' : 's'} renew · {valueLabel} contracted
          </div>
        </div>
      </div>
      <div className="cov-horizon-list">
        {buckets.map((b, i) => {
          if (b.agreements.length === 0) return null
          const urgency: 'danger' | 'warn' | 'ok' =
            i < 3 ? 'danger' : i < 6 ? 'warn' : 'ok'
          return (
            <div key={i} className="cov-horizon-month">
              <div className={`cov-horizon-mlabel ${urgency}`}>{b.label}</div>
              <div className="cov-horizon-chips">
                {b.agreements.map(({ a, daysToEnd }) => {
                  const chipUrgency = daysToEnd < 90 ? 'danger' : daysToEnd < 180 ? 'warn' : 'ok'
                  return (
                    <Link
                      key={a.id}
                      to="/customers/$id"
                      params={{ id: String(a.customerId) }}
                      className={`cov-horizon-chip ${chipUrgency}`}
                    >
                      <span className="nm">{a.customerName ?? a.title}</span>
                      <span className="val">
                        {contractTypeLabel(a.contractType)}
                        {a.value != null && (
                          <> · {a.value >= 1_000_000
                            ? `${(a.value / 1_000_000).toFixed(1)} M`
                            : `${Math.round(a.value / 1000)} k`} {a.currency}</>)}
                      </span>
                    </Link>
                  )
                })}
              </div>
              {b.totalLabel && (
                <div className="cov-horizon-total">{b.totalLabel}</div>
              )}
            </div>
          )
        })}
      </div>
      <div className="cov-horizon-stats">
        <div className="stat">
          <div className="lbl">Contracted value · 12 mo</div>
          <div className="val">{valueLabel} <span className="unit">SEK</span></div>
          <div className="sub">{total} agreement{total === 1 ? '' : 's'}</div>
        </div>
        <div className="stat">
          <div className="lbl">Up for review · 90 days</div>
          <div className="val" style={{ color: 'var(--ns-err)' }}>
            {expiring90Label} <span className="unit">SEK</span>
          </div>
          <div className="sub">
            {buckets.slice(0, 3).reduce((s, b) => s + b.agreements.length, 0)} agreement
            {buckets.slice(0, 3).reduce((s, b) => s + b.agreements.length, 0) === 1 ? '' : 's'}
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── List tab ─────────────────────────────────────────────────────────────────

type SortKey =
  | 'name' | 'manager' | 'contract' | 'projects' | 'hours' | 'util' | 'status'

interface ListFilters {
  q:       string
  manager: string
  contract: 'frame' | 'msa' | 'service' | 'none' | ''
  status:  'active' | 'inactive' | ''
}

function ListTab({
  stats, onEdit,
}: {
  stats:  CustomerStat[]
  onEdit: (c: Customer) => void
}) {
  const navigate = useNavigate()
  // Default status filter to "active" — list now includes inactive customers
  // (WI-11999) so the previous implicit active-only behaviour needs an
  // explicit default to match user expectations.
  const [filters, setFilters] = useState<ListFilters>({
    q: '', manager: '', contract: '', status: 'active',
  })
  const [sortKey, setSortKey] = useState<SortKey>('hours')
  const [sortAsc, setSortAsc] = useState(false)
  const [compact, setCompact] = useState(false)

  const managers = useMemo(() => {
    const set = new Map<number, string>()
    for (const s of stats) {
      if (s.customer.accountManagerId != null && s.customer.accountManagerName) {
        set.set(s.customer.accountManagerId, s.customer.accountManagerName)
      }
    }
    return [...set.values()].sort()
  }, [stats])

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase()
    return stats.filter(s => {
      const c = s.customer
      if (q) {
        const hay = [c.name, c.city, c.country, c.accountManagerName].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (filters.manager && c.accountManagerName !== filters.manager) return false
      if (filters.contract) {
        const t = s.contract?.agreement.contractType
        if (filters.contract === 'frame'   && t !== 'framework') return false
        if (filters.contract === 'msa'     && t !== 'msa')       return false
        if (filters.contract === 'service' && t !== 'service')   return false
        if (filters.contract === 'none'    && s.contract != null) return false
      }
      if (filters.status === 'active'   && !c.isActive) return false
      if (filters.status === 'inactive' && c.isActive)  return false
      return true
    })
  }, [stats, filters])

  const sorted = useMemo(() => {
    const key = sortKey
    const dir = sortAsc ? 1 : -1
    const fns: Record<SortKey, (s: CustomerStat) => string | number> = {
      name:     s => s.customer.name.toLowerCase(),
      manager:  s => s.customer.accountManagerName?.toLowerCase() ?? '￿',
      contract: s => s.contract?.agreement.contractType ?? 'zzzz',
      projects: s => s.activeProjects,
      hours:    s => s.totalHours,
      util:     s => s.utilizationPct ?? -1,
      status:   s => (s.customer.isActive ? 0 : 1),
    }
    const f = fns[key]
    return [...filtered].sort((a, b) => {
      const va = f(a), vb = f(b)
      if (va < vb) return -1 * dir
      if (va > vb) return  1 * dir
      return 0
    })
  }, [filtered, sortKey, sortAsc])

  const setF = <K extends keyof ListFilters>(k: K, v: ListFilters[K]) =>
    setFilters(prev => ({ ...prev, [k]: v }))

  const resetAll = () => setFilters({ q: '', manager: '', contract: '', status: 'active' })

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortAsc(v => !v)
    else { setSortKey(k); setSortAsc(k === 'name' || k === 'manager' || k === 'contract') }
  }

  // status === 'active' is the default since WI-11999; only surface a chip
  // for non-default status values (inactive, all).
  const hasNonDefaultStatus = filters.status !== '' && filters.status !== 'active'
  const hasFilters = !!(filters.q || filters.manager || filters.contract || hasNonDefaultStatus)

  return (
    <>
      <section className="cov-filter-bar">
        <div className={`cov-filt search ${filters.q ? 'has-value' : ''}`}>
          <span className="magnify"><Search size={14} /></span>
          <input
            type="text"
            placeholder="Search customer, city, manager…"
            value={filters.q}
            onChange={e => setF('q', e.target.value)}
          />
        </div>
        <div className={`cov-filt ${filters.manager ? 'has-value' : ''}`}>
          <label>Account manager</label>
          <select value={filters.manager} onChange={e => setF('manager', e.target.value)}>
            <option value="">All</option>
            {managers.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <span className="caret"><ChevronDown size={12} /></span>
        </div>
        <div className={`cov-filt ${filters.contract ? 'has-value' : ''}`}>
          <label>Contract</label>
          <select
            value={filters.contract}
            onChange={e => setF('contract', e.target.value as ListFilters['contract'])}
          >
            <option value="">All</option>
            <option value="frame">Framework</option>
            <option value="msa">MSA</option>
            <option value="service">Service</option>
            <option value="none">None</option>
          </select>
          <span className="caret"><ChevronDown size={12} /></span>
        </div>
        <div className={`cov-filt ${hasNonDefaultStatus ? 'has-value' : ''}`}>
          <label>Status</label>
          <select
            value={filters.status}
            onChange={e => setF('status', e.target.value as ListFilters['status'])}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="caret"><ChevronDown size={12} /></span>
        </div>
        <button className="cov-filt-clear" onClick={resetAll} disabled={!hasFilters}>
          <X size={13} />Reset
        </button>
      </section>

      {hasFilters && (
        <div className="cov-filter-chips">
          <span className="cov-chip-label">Active filters:</span>
          {filters.q && (
            <span className="cov-fchip">
              "{filters.q}"<button onClick={() => setF('q', '')} aria-label="Clear">×</button>
            </span>
          )}
          {filters.manager && (
            <span className="cov-fchip">
              Manager: {filters.manager}<button onClick={() => setF('manager', '')} aria-label="Clear">×</button>
            </span>
          )}
          {filters.contract && (
            <span className="cov-fchip">
              Contract: {filters.contract.toUpperCase()}<button onClick={() => setF('contract', '')} aria-label="Clear">×</button>
            </span>
          )}
          {hasNonDefaultStatus && (
            <span className="cov-fchip">
              Status: {filters.status === '' ? 'all' : filters.status}
              <button onClick={() => setF('status', 'active')} aria-label="Clear">×</button>
            </span>
          )}
        </div>
      )}

      <section className="cov-table-wrap">
        <div className="cov-table-toolbar">
          <div>
            <span className="ttitle">Customers</span>
            <span className="tcount">{sorted.length} of {stats.length} shown</span>
          </div>
          <div className="tools">
            <div className="cov-density" role="tablist" aria-label="Row density">
              <button
                aria-label="Comfortable"
                className={!compact ? 'on' : ''}
                onClick={() => setCompact(false)}
              >
                <Rows3 size={14} />
              </button>
              <button
                aria-label="Compact"
                className={compact ? 'on' : ''}
                onClick={() => setCompact(true)}
              >
                <Rows4 size={14} />
              </button>
            </div>
          </div>
        </div>

        <table className={`cov-gtable ${compact ? 'compact' : ''}`}>
          <thead>
            <tr>
              <SortHeader k="name"     sortKey={sortKey} sortAsc={sortAsc} onSort={onSort}>Customer</SortHeader>
              <SortHeader k="manager"  sortKey={sortKey} sortAsc={sortAsc} onSort={onSort}>Account manager</SortHeader>
              <SortHeader k="contract" sortKey={sortKey} sortAsc={sortAsc} onSort={onSort}>Contract</SortHeader>
              <SortHeader k="projects" sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} align="right">Projects</SortHeader>
              <SortHeader k="hours"    sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} align="right">Hours / Budget</SortHeader>
              <SortHeader k="util"     sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} align="right">Utilization</SortHeader>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(s => (
              <ListRow
                key={s.customer.id}
                stat={s}
                onOpen={() => navigate({ to: '/customers/$id', params: { id: String(s.customer.id) } })}
                onEdit={() => onEdit(s.customer)}
              />
            ))}
          </tbody>
        </table>

        {sorted.length === 0 && (
          <div className="cov-empty">
            <div className="big">No customers match these filters</div>
            <div>Try removing the contract or manager filter.</div>
          </div>
        )}
      </section>
    </>
  )
}

function SortHeader({
  k, sortKey, sortAsc, onSort, align, children,
}: {
  k:        SortKey
  sortKey:  SortKey
  sortAsc:  boolean
  onSort:   (k: SortKey) => void
  align?:   'right'
  children: React.ReactNode
}) {
  const active = sortKey === k
  return (
    <th className={align === 'right' ? 'r' : ''}>
      <span
        className={`sortable ${active ? 'active' : ''}`}
        onClick={() => onSort(k)}
      >
        {children}
        {active && <span aria-hidden="true">{sortAsc ? '↑' : '↓'}</span>}
      </span>
    </th>
  )
}

function ListRow({
  stat, onOpen, onEdit,
}: {
  stat:   CustomerStat
  onOpen: () => void
  onEdit: () => void
}) {
  const { customer: c, projects, totalHours, budgetHours, contract, activeProjects } = stat
  const utilization = stat.utilizationPct
  const utilWarn = utilization != null && utilization > 60

  return (
    <tr onClick={onOpen}>
      <td>
        <div className="cov-cust-cell">
          <div className="ph" style={customerLogoStyle(c.name)}>{initials(c.name)}</div>
          <div className="col">
            <span className="name">{c.name}</span>
            <span className="a">
              {c.city ?? '—'}{c.country && c.country !== 'Sweden' ? `, ${c.country}` : ''}
              {projects.length > 0 && <> · {projects.length} project{projects.length === 1 ? '' : 's'}</>}
            </span>
          </div>
        </div>
      </td>
      <td>
        {c.accountManagerName ? (
          <div className="cov-lb-mgr">
            <div className="av" style={avatarStyle(c.accountManagerName)}>
              {initials(c.accountManagerName)}
            </div>
            {c.accountManagerName}
          </div>
        ) : (
          <span style={{ color: 'var(--ns-text-subtle)', fontSize: 12 }}>—</span>
        )}
      </td>
      <td>
        <ContractPill contract={contract} />
      </td>
      <td className="r">
        {activeProjects > 0 ? (
          <><b>{activeProjects}</b> <span style={{ color: 'var(--ns-text-subtle)', fontSize: 11 }}>active</span></>
        ) : (
          <span style={{ color: 'var(--ns-text-subtle)' }}>0</span>
        )}
      </td>
      <td className="r">
        {fmtHours(totalHours)}
        {budgetHours != null && <> / {fmtHours(budgetHours)} h</>}
      </td>
      <td className="r">
        {utilization != null ? (
          <div className="cov-util-cell">
            <div className={`pbar ${utilWarn ? 'warn' : ''}`}>
              <div style={{ width: `${Math.min(100, utilization)}%` }} />
            </div>
            <span className={`v ${utilWarn ? 'warn' : ''}`}>{utilization}%</span>
          </div>
        ) : (
          <span style={{ color: 'var(--ns-text-subtle)', fontSize: 12 }}>—</span>
        )}
      </td>
      <td onClick={e => e.stopPropagation()}>
        <button
          className="cov-edit-pencil"
          title="Edit customer"
          onClick={onEdit}
        >
          <Pencil size={13} />
        </button>
      </td>
    </tr>
  )
}
